import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLenis } from '@/hooks/useLenis';
import { FadeSection } from '@/components/shared/FadeSection';
import { Check, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import '@/styles/contact.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  useLenis();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) return;
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: name,
        email: email,
        message: message
      });
      
      if (response.data.success) {
        setSubmitted(true);
      } else {
        toast.error(response.data.message || 'Something went wrong.');
      }
    } catch (error) {
      toast.error('Unable to send message. Please try again.');
    }
    
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="contact-page" data-testid="contact-page">
        <div className="noise-overlay" />
        
        <section className="contact-success">
          <div className="contact-success-inner">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="contact-success-icon"
            >
              <Check size={32} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="contact-success-headline"
            >
              Message sent
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="contact-success-body"
              data-testid="contact-success-body"
            >
              We&rsquo;ve received your message. For privacy and safety enquiries, messages are reviewed directly.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="contact-success-note"
              data-testid="contact-success-note"
            >
              We&rsquo;ll usually respond once reviewed.
            </motion.p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="contact-page" data-testid="contact-page">
      <div className="noise-overlay" />
      
      {/* Header */}
      <section className="contact-header">
        <div className="contact-header-inner">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="contact-headline"
          >
            Get in touch
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="contact-subtext"
          >
            We're here to help.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="contact-main">
        <div className="contact-main-inner">
          
          {/* Info Column */}
          <div className="contact-info">
            <FadeSection>
              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <Mail size={18} />
                </div>
                <div className="contact-info-content">
                  <p className="contact-info-label">Email us directly</p>
                  <a href="mailto:support@nymphe.app" className="contact-info-value">support@nymphe.app</a>
                </div>
              </div>
            </FadeSection>
            
            <FadeSection delay={0.1}>
              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <Clock size={18} />
                </div>
                <div className="contact-info-content">
                  <p className="contact-info-label">Response time</p>
                  <p className="contact-info-value">Usually within 48 hours</p>
                </div>
              </div>
            </FadeSection>

            <FadeSection delay={0.18}>
              <div className="contact-enquiries" data-testid="contact-enquiries">
                <p className="contact-enquiries-label">Enquiries we welcome</p>
                <ul className="contact-enquiries-list">
                  <li>Account &amp; access questions</li>
                  <li>Privacy enquiries &mdash; reviewed directly.</li>
                  <li>Safety concerns &mdash; reviewed directly.</li>
                  <li>Press &amp; general questions</li>
                </ul>
              </div>
            </FadeSection>

            <FadeSection delay={0.26}>
              <div className="contact-info-note">
                <p>For urgent safety concerns,</p>
                <p>please contact local authorities.</p>
              </div>
            </FadeSection>
          </div>

          {/* Form Column */}
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <FadeSection className="contact-field-group">
                <label className="contact-label">Your name</label>
                <input
                  type="text"
                  className="contact-input"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="contact-name"
                  required
                />
              </FadeSection>

              <FadeSection delay={0.08} className="contact-field-group">
                <label className="contact-label">Your email</label>
                <input
                  type="email"
                  className="contact-input"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="contact-email"
                  required
                />
              </FadeSection>

              <FadeSection delay={0.16} className="contact-field-group">
                <label className="contact-label">How can we help?</label>
                <textarea
                  className="contact-textarea"
                  placeholder="Your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  data-testid="contact-message"
                  required
                />
              </FadeSection>

              <FadeSection delay={0.24} className="contact-submit-group">
                <button
                  type="submit"
                  className="contact-submit-btn"
                  disabled={!name || !email || !message || submitting}
                  data-testid="contact-submit"
                >
                  {submitting ? 'Sending...' : 'Send message'}
                </button>
              </FadeSection>
            </form>
          </div>

        </div>
      </section>

      {/* Reassurance */}
      <section className="contact-reassurance">
        <FadeSection>
          <p className="contact-reassurance-text">
            Your message is handled privately and reviewed by the appropriate NYMPHÉ team where needed.
          </p>
        </FadeSection>
        <FadeSection delay={0.1}>
          <p className="contact-reassurance-support" data-testid="contact-support-line">
            For privacy or safety enquiries, we review messages directly and handle them with care.
          </p>
        </FadeSection>
      </section>
    </div>
  );
};

export default ContactPage;
